import React, { useMemo, useCallback, memo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { closeModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from '../StandardModal';
import { FormSelect } from 'components/Form';

const UpdateOrganizationMemberModal = memo(({ options }) => {
	const dispatch = useDispatch();
	const { actions } = useOrganizations();

	const organizationId = options?.organizationId;
	const member = options?.member;
	const onSuccess = options?.onSuccess;

	// React Hook Form
	const { control, handleSubmit, formState } = useForm({
		defaultValues: {
			role: member?.role || 'member',
		},
		mode: 'onChange',
	});

	// Memoized role options
	const roleOptions = useMemo(
		() => [
			{ value: 'admin', label: 'Admin - Full management access' },
			{ value: 'member', label: 'Member - Standard access' },
			{ value: 'viewer', label: 'Viewer - Read-only access' },
		],
		[],
	);

	// Form submit handler
	const onSubmit = useCallback(
		async (data) => {
			try {
				await actions.updateMember({
					id: organizationId,
					userId: member.id,
					data,
				});

				dispatch(
					showToast({
						title: 'Success',
						body: 'Member role updated successfully',
						type: 'success',
					}),
				);

				if (onSuccess) {
					onSuccess();
				}

				dispatch(closeModal());
			} catch (error) {
				dispatch(
					showToast({
						title: 'Error',
						body: error.message || 'Failed to update member role',
						type: 'error',
					}),
				);
			}
		},
		[organizationId, member, actions, dispatch, onSuccess],
	);

	const handleCancel = useCallback(() => {
		dispatch(closeModal());
	}, [dispatch]);

	return (
		<StandardModal
			title="Update Member Role"
			size="md"
			onClose={handleCancel}
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="modal-body">
					<div className="mb-4">
						<div className="d-flex align-items-center">
							<div
								className="organization-members__avatar me-3"
								style={{
									width: '48px',
									height: '48px',
									borderRadius: '50%',
									background:
										'linear-gradient(135deg, var(--bs-primary), var(--bs-active-primary))',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: 'white',
									fontWeight: 'bold',
									fontSize: '18px',
								}}
							>
								{member?.name?.charAt(0)?.toUpperCase() || 'U'}
							</div>
							<div>
								<div className="fw-bold">{member?.name}</div>
								<div className="text-muted small">{member?.email}</div>
							</div>
						</div>
					</div>

					<FormSelect
						name="role"
						control={control}
						label="New Role"
						options={roleOptions}
						required
					/>

					<div className="alert alert-warning mt-3">
						<i className="bi bi-exclamation-triangle me-2"></i>
						<strong>Warning:</strong> Changing a user's role will immediately
						update their permissions in this organization.
					</div>
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleCancel}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={!formState.isValid}
					>
						<i className="bi bi-check-lg me-2" />
						Update Role
					</button>
				</div>
			</form>
		</StandardModal>
	);
});

UpdateOrganizationMemberModal.displayName = 'UpdateOrganizationMemberModal';

export default UpdateOrganizationMemberModal;
